import { getQuotationTemplatePreviewPdf } from '../api';

/**
 * Global in-memory cache of object URLs for sample PDFs.
 * Persists for the lifetime of the browser session so PDFs are never re-fetched or re-rendered.
 */
const blobUrlCache = new Map();
const inFlightPromises = new Map();

/**
 * Priority-aware concurrency queue.
 * Limits simultaneous PDF generation requests to 2 to prevent backend CPU choking,
 * ensuring visible and selected templates load in ~500ms instead of timing out.
 */
class RequestQueue {
  constructor(concurrency = 2) {
    this.concurrency = concurrency;
    this.running = 0;
    this.queue = [];
  }

  enqueue(fn, priority = 0) {
    return new Promise((resolve, reject) => {
      this.queue.push({ fn, priority, resolve, reject });
      // Highest priority executed first
      this.queue.sort((a, b) => b.priority - a.priority);
      this.dequeue();
    });
  }

  dequeue() {
    if (this.running >= this.concurrency || this.queue.length === 0) return;
    const item = this.queue.shift();
    if (!item) return;
    const { fn, resolve, reject } = item;
    this.running++;
    fn()
      .then(resolve)
      .catch(reject)
      .finally(() => {
        this.running--;
        this.dequeue();
      });
  }
}

const pdfQueue = new RequestQueue(2);

/** Synchronously returns cached blob URL if already loaded */
export function getCachedPdfBlobUrl(companyId, templateKey) {
  if (!companyId || !templateKey) return null;
  return blobUrlCache.get(`${companyId}:${templateKey}`) || null;
}

/**
 * Enqueues or returns the cached blob URL for a template's sample PDF.
 * Deduplicates in-flight requests and executes with priority.
 */
export function loadPdfPreviewBlobUrl(companyId, templateKey, priority = 0) {
  if (!companyId || !templateKey) return Promise.resolve(null);
  const cacheKey = `${companyId}:${templateKey}`;

  if (blobUrlCache.has(cacheKey)) {
    return Promise.resolve(blobUrlCache.get(cacheKey));
  }

  if (inFlightPromises.has(cacheKey)) {
    return inFlightPromises.get(cacheKey);
  }

  const promise = pdfQueue
    .enqueue(async () => {
      if (blobUrlCache.has(cacheKey)) {
        return blobUrlCache.get(cacheKey);
      }
      const blob = await getQuotationTemplatePreviewPdf({
        companyId,
        templateKey,
      });
      const blobUrl = URL.createObjectURL(blob);
      blobUrlCache.set(cacheKey, blobUrl);
      return blobUrl;
    }, priority)
    .finally(() => {
      inFlightPromises.delete(cacheKey);
    });

  inFlightPromises.set(cacheKey, promise);
  return promise;
}

/**
 * Batch preload helper for a list of templates.
 * Assigns descending priority so the first visible templates load first.
 */
export function preloadPdfPreviews(
  companyId,
  templates = [],
  selectedKey = null,
) {
  if (!companyId || !Array.isArray(templates) || templates.length === 0) return;

  templates.forEach((t, idx) => {
    const key = t?.key;
    if (!key || blobUrlCache.has(`${companyId}:${key}`)) return;

    // Highest priority for selected, then descending by position in grid
    const priority = key === selectedKey ? 20 : Math.max(1, 10 - idx);
    loadPdfPreviewBlobUrl(companyId, key, priority).catch(() => {
      // Ignored in background preloader
    });
  });
}
